import { useEffect, useRef, useState } from 'react'

interface PerpLine {
  x1: number
  y1: number
  x2: number
  y2: number
  fraction: number // where on the spiral path this line sits (0-1)
}

// CSS ease-out is cubic-bezier(0, 0, 0.58, 1).
// Solve the cubic-bezier to match the actual CSS animation progress.
function cubicBezierY(x: number): number {
  // Control points: P0=(0,0) P1=(0,0) P2=(0.58,1) P3=(1,1)
  // Solve for parameter t where bezier-x(t) = x, then return bezier-y(t).
  // x(t) = 3*(1-t)^2*t*0 + 3*(1-t)*t^2*0.58 + t^3 = 1.74*t^2*(1-t) + t^3
  // y(t) = 3*(1-t)^2*t*0 + 3*(1-t)*t^2*1 + t^3 = 3*t^2*(1-t) + t^3 = 3*t^2 - 2*t^3
  // Newton's method to find t from x
  let t = x
  for (let i = 0; i < 8; i++) {
    const xt = 1.74 * t * t * (1 - t) + t * t * t - x
    const dxt = 3.48 * t * (1 - t) - 1.74 * t * t + 3 * t * t
    if (Math.abs(dxt) < 1e-10) break
    t -= xt / dxt
  }
  return 3 * t * t - 2 * t * t * t
}

const DRAW_DURATION = 3000 // ms
const DASH_START_FRACTION = 0.7 // where along the spiral (0-1) the dashes begin
const DASH_HEAD_START = 0.04 // how far ahead of the spiral tip dashes appear (0 = exact sync)
const DASH_LENGTH = 60 // base length of each dash line in px
const NUM_DASHES = 20 // number of perpendicular dash lines

export function SymbalismPage() {
  const [isVisible, setIsVisible] = useState(true)
  const [perpLines, setPerpLines] = useState<PerpLine[]>([])
  const [drawnFraction, setDrawnFraction] = useState(0)
  const pathRef = useRef<SVGPathElement>(null)
  const startTimeRef = useRef<number>(0)

  useEffect(() => {
    const path = pathRef.current
    if (!path) return

    const pathLength = path.getTotalLength()
    path.style.strokeDasharray = `${pathLength}`
    path.style.strokeDashoffset = `${pathLength}`

    // Compute perpendicular lines up front
    const lines: PerpLine[] = []
    const startFraction = DASH_START_FRACTION
    const numLines = NUM_DASHES
    const lineLength = DASH_LENGTH

    for (let i = 0; i < numLines; i++) {
      const t = startFraction + (i / (numLines - 1)) * (1 - startFraction)
      const dist = t * pathLength
      const point = path.getPointAtLength(dist)

      const delta = 0.5
      const before = path.getPointAtLength(Math.max(0, dist - delta))
      const after = path.getPointAtLength(Math.min(pathLength, dist + delta))
      const dx = after.x - before.x
      const dy = after.y - before.y
      const len = Math.sqrt(dx * dx + dy * dy)

      if (len === 0) continue

      let nx = -dy / len
      let ny = dx / len

      const toCenterX = 300 - point.x
      const toCenterY = 300 - point.y
      const dot = nx * toCenterX + ny * toCenterY
      if (dot > 0) {
        nx = -nx
        ny = -ny
      }

      const wobble = (Math.random() - 0.5) * 4
      const lengthVar = lineLength + (Math.random() - 0.5) * 12

      lines.push({
        x1: point.x + wobble,
        y1: point.y + wobble,
        x2: point.x + nx * lengthVar + wobble,
        y2: point.y + ny * lengthVar + wobble,
        fraction: t,
      })
    }

    setPerpLines(lines)

    // Track how far the spiral has drawn using requestAnimationFrame
    startTimeRef.current = performance.now()

    let animFrame: number
    const tick = (now: number) => {
      const elapsed = now - startTimeRef.current
      const progress = Math.min(elapsed / DRAW_DURATION, 1)
      const easedProgress = cubicBezierY(progress)
      setDrawnFraction(easedProgress)

      if (progress < 1) {
        animFrame = requestAnimationFrame(tick)
      }
    }
    animFrame = requestAnimationFrame(tick)

    // Fade out: spiral (3s) + stay (3s)
    // const fadeTimer = setTimeout(() => {
    //   setIsVisible(false)
    // }, 6000)

    // return () => {
    //   cancelAnimationFrame(animFrame)
    //   clearTimeout(fadeTimer)
    // }
  }, [])

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa',
        opacity: isVisible ? 1 : 0,
        transition: 'opacity 1.5s ease-out',
      }}
    >
      <svg
        width="600"
        height="600"
        viewBox="0 0 600 600"
        style={{
          filter: 'drop-shadow(2px 2px 3px rgba(0,0,0,0.1))',
        }}
      >
        <path
          ref={pathRef}
          d="M 300,300
             C 300,280 320,280 320,300
             C 320,330 270,330 270,300
             C 270,250 350,250 350,300
             C 350,370 230,370 230,300
             C 230,210 390,210 390,300
             C 390,410 190,410 190,300
             C 190,170 430,170 430,300
             C 430,450 150,450 150,300
             C 150,130 470,130 470,300
             C 470,490 110,490 110,300
             C 110,90 510,90 510,300
             C 510,530 70,530 70,300"
          fill="none"
          stroke="#1d1d1d"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            strokeDasharray: 9999,
            strokeDashoffset: 9999,
            animation: 'draw 3s ease-out forwards',
          }}
        />

        {perpLines.map((line, i) => {
          // Line only appears once spiral has reached this point
          const reached = drawnFraction >= line.fraction - DASH_HEAD_START
          // How far past the point we are (used to animate the line drawing in)
          const lineProgress = reached
            ? Math.min((drawnFraction - line.fraction) / 0.03, 1)
            : 0

          return (
            <line
              key={i}
              x1={line.x1}
              y1={line.y1}
              x2={line.x1 + (line.x2 - line.x1) * lineProgress}
              y2={line.y1 + (line.y2 - line.y1) * lineProgress}
              stroke="#1d1d1d"
              strokeWidth="2"
              strokeLinecap="round"
              style={{
                opacity: lineProgress,
              }}
            />
          )
        })}

        <style>
          {`
            @keyframes draw {
              to {
                stroke-dashoffset: 0;
              }
            }
          `}
        </style>
      </svg>
    </div>
  )
}
